import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaTrash, FaUsers, FaCog, FaEye, FaSignInAlt } from "react-icons/fa";
import type { ColumnsType } from "antd/es/table";
import {
  Button,
  Input,
  Message,
  Popconfirm,
  Table,
  Tabs,
  Tag,
} from "@/frontend/components/common";
import { bettingPoolService } from "@/frontend/services";
import { useAuthStore } from "@/frontend/stores";
import { AdminGuardEnum } from "@/core/enums";
import type {
  BettingPoolWithDetailsResponseDTO,
  PaginationRequestDTO,
} from "@/shared/dto";
import type { TablePaginationConfig } from "antd/es/table";

type PoolsTabKey = "owned" | "joined";

export function MyBettingPoolsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const isAdmin = hasPermission(AdminGuardEnum.ADMIN_ACCESS);
  const [activeTab, setActiveTab] = useState<PoolsTabKey>(isAdmin ? "owned" : "joined");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<BettingPoolWithDetailsResponseDTO[]>([]);
  const [searchName, setSearchName] = useState("");
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 7,
    total: 0,
  });

  const loadData = async (tab: PoolsTabKey, params?: PaginationRequestDTO) => {
    try {
      setLoading(true);
      const fetchPools =
        tab === "owned"
          ? bettingPoolService.getMyPools
          : bettingPoolService.getJoinedPools;

      const response = await fetchPools({
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? pagination.pageSize ?? 7,
        name: searchName || undefined,
      });

      if (response.success && response.data) {
        setData(response.data.data);
        setPagination((prev) => ({
          ...prev,
          current: response.data!.pagination.page,
          pageSize: response.data!.pagination.pageSize,
          total: response.data!.pagination.total,
        }));
      } else {
        Message.error({
          content: response.error || "Error al cargar los pools",
        });
      }
    } catch (error) {
      Message.error({
        content:
          error instanceof Error ? error.message : "Error al cargar los pools",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(activeTab, { page: 1, pageSize: pagination.pageSize ?? 7 });
  }, [activeTab]);

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    loadData(activeTab, {
      page: newPagination.current ?? 1,
      pageSize: newPagination.pageSize ?? 7,
    });
  };

  const handleSearch = () => {
    loadData(activeTab, { page: 1, pageSize: pagination.pageSize ?? 7 });
  };

  const handleTabChange = (key: string) => {
    setSearchName("");
    setActiveTab(key as PoolsTabKey);
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await bettingPoolService.delete(id);
      if (response.success) {
        Message.success({ content: "Pool eliminado correctamente" });
        await loadData(activeTab);
      } else {
        Message.error({ content: response.error || "Error al eliminar pool" });
      }
    } catch (error) {
      Message.error({
        content:
          error instanceof Error ? error.message : "Error al eliminar pool",
      });
    }
  };

  const baseColumns: ColumnsType<BettingPoolWithDetailsResponseDTO> = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      render: (name: string, record) => (
        <div>
          <div className="font-medium">{name}</div>
          {record.description && (
            <div className="text-gray-500 text-sm">{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: "Estado",
      key: "status",
      render: (_, record) =>
        record.status ? (
          <Tag color={record.status.color || "default"}>
            {record.status.name}
          </Tag>
        ) : (
          <Tag color="default">Sin estado</Tag>
        ),
    },
    {
      title: "Participantes",
      key: "participantsCount",
      render: (_, record) => (
        <div className="flex items-center gap-1">
          <FaUsers className="text-gray-400" />
          <span>{record.participantsCount}</span>
        </div>
      ),
    },
  ];

  const ownedColumns: ColumnsType<BettingPoolWithDetailsResponseDTO> = [
    ...baseColumns,
    {
      title: "Código de invitación",
      dataIndex: "inviteCode",
      key: "inviteCode",
      render: (code: string) => (
        <code className="bg-gray-100 px-2 py-1 rounded text-sm">{code}</code>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            type="link"
            icon={<FaCog />}
            onClick={() => navigate(`/sports-predictions/pools/${record.id}`)}
          >
            Configuración
          </Button>
          <Popconfirm
            title="¿Eliminar pool?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger icon={<FaTrash />}>
              Eliminar
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const joinedColumns: ColumnsType<BettingPoolWithDetailsResponseDTO> = [
    ...baseColumns,
    {
      title: "Acciones",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          icon={<FaEye />}
          onClick={() => navigate(`/sports-predictions/pools/${record.id}/predictions`)}
        >
          Predecir
        </Button>
      ),
    },
  ];

  const columns = activeTab === "owned" ? ownedColumns : joinedColumns;

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Mis grupos de apuestas
          </h2>
          <p className="text-gray-600 mt-1">
            {!isAdmin
              ? "Grupos a los que has sido invitado."
              : activeTab === "owned"
                ? "Administra los grupos de apuestas que has creado."
                : "Grupos de apuestas en los que participas."}
          </p>
        </div>
        {isAdmin ? (
          <Button
            type="primary"
            icon={<FaPlus />}
            onClick={() => navigate("/sports-predictions/pools/create")}
          >
            Crear nuevo grupo
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<FaSignInAlt />}
            onClick={() => navigate("/join")}
          >
            Unirme a un grupo
          </Button>
        )}
      </div>

      {isAdmin && (
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            { key: "owned", label: "Mis grupos creados" },
            { key: "joined", label: "Grupos en los que participo" },
          ]}
        />
      )}

      <div className="flex items-center gap-2 mb-4">
        <Input
          size="large"
          placeholder="Buscar por nombre"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          onPressEnter={handleSearch}
          className="max-w-xs"
        />
        <Button type="primary" onClick={handleSearch}>
          Buscar
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={pagination}
        onChange={handleTableChange}
      />
    </div>
  );
}
